import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../context/AuthContext';
import { NavigationContext } from '../../context/NavigationContext';
import { toast } from 'react-toastify';

interface TravelbingoEntry {
    id: number;
    createdAt: string;
    name: string;
    filename: string;
    status: string;
}

const TravelbingoEvaluationDetails: React.FC = () => {
    const authContext = useContext(AuthContext);
    const navigationContext = useContext(NavigationContext);
    const [entry, setEntry] = useState<TravelbingoEntry | null>(null);
    const [loading, setLoading] = useState(true);

    if (!authContext) {
        throw new Error('TravelbingoEvaluationDetails must be used within an AuthProvider');
    }

    if (!navigationContext) {
        throw new Error('TravelbingoEvaluationDetails must be used within a NavigationProvider');
    }

    const { token } = authContext;
    const { navigateTo, currentPath } = navigationContext;

    // Extract ID from path (e.g., /travelbingo/evaluations/123)
    const id = currentPath.split('/').pop();

    useEffect(() => {
        if (id && !isNaN(Number(id))) {
            fetchEvaluationDetails(Number(id));
        } else {
            toast.error('Ungültige Auswertungs-ID');
            setLoading(false);
        }
    }, [id]);

    const fetchEvaluationDetails = async (evaluationId: number) => {
        setLoading(true);
        try {
            const response = await fetch(`/api/travelbingo/${evaluationId}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                }
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Fehler beim Laden der Auswertung');
            }

            const data: TravelbingoEntry = await response.json();
            setEntry(data);
        } catch (error) {
            console.error('Error fetching travelbingo evaluation details:', error);
            toast.error(error instanceof Error ? error.message : 'Fehler beim Laden der Auswertung');
        } finally {
            setLoading(false);
        }
    };

    const handleBack = () => {
        navigateTo('/travelbingo/evaluations');
    };

    if (loading) {
        return (
            <div style={{ padding: '20px' }}>
                <h1>Auswertung Details</h1>
                <p>Wird geladen...</p>
            </div>
        );
    }

    if (!entry) {
        return (
            <div style={{ padding: '20px' }}>
                <h1>Auswertung Details</h1>
                <p>Auswertung nicht gefunden.</p>
                <button
                    onClick={handleBack}
                    style={{
                        padding: '10px 20px',
                        fontSize: '16px',
                        backgroundColor: '#6c757d',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        marginTop: '20px'
                    }}
                >
                    Zurück zur Liste
                </button>
            </div>
        );
    }

    return (
        <div style={{ padding: '20px' }}>
            <h1>Auswertung Details</h1>
            
            <div style={{
                marginTop: '20px',
                padding: '20px',
                backgroundColor: 'white',
                borderRadius: '8px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}>
                <div style={{ marginBottom: '20px' }}>
                    <h2 style={{ marginTop: 0, marginBottom: '20px', color: '#333' }}>{entry.name}</h2>
                    
                    <div style={{ display: 'grid', gridTemplateColumns: '150px 1fr', gap: '15px', fontSize: '16px' }}>
                        <div style={{ fontWeight: 'bold', color: '#555' }}>ID:</div>
                        <div>{entry.id}</div>
                        
                        <div style={{ fontWeight: 'bold', color: '#555' }}>Name:</div>
                        <div>{entry.name}</div>
                        
                        <div style={{ fontWeight: 'bold', color: '#555' }}>Erstellt am:</div>
                        <div>
                            {new Date(entry.createdAt).toLocaleString('de-DE', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                                second: '2-digit'
                            })}
                        </div>
                        
                        <div style={{ fontWeight: 'bold', color: '#555' }}>Dateiname:</div>
                        <div>{entry.filename}</div>
                        
                        <div style={{ fontWeight: 'bold', color: '#555' }}>Status:</div>
                        <div>
                            <span style={{
                                padding: '6px 12px',
                                borderRadius: '4px',
                                backgroundColor: entry.status === 'new' ? '#d1ecf1' : '#d4edda',
                                color: entry.status === 'new' ? '#0c5460' : '#155724',
                                fontSize: '14px'
                            }}>
                                {entry.status}
                            </span>
                        </div>
                    </div>
                </div>

                <div style={{ marginTop: '30px', paddingTop: '20px', borderTop: '1px solid #dee2e6' }}>
                    <h3 style={{ marginBottom: '15px', color: '#333' }}>Weitere Informationen</h3>
                    <p style={{ color: '#666' }}>
                        Diese Auswertung wurde am {new Date(entry.createdAt).toLocaleDateString('de-DE')} erstellt.
                    </p>
                </div>
            </div>

            <button
                onClick={handleBack}
                style={{
                    padding: '10px 20px',
                    fontSize: '16px',
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    marginTop: '20px'
                }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#5a6268'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#6c757d'}
            >
                Zurück zur Liste
            </button>
        </div>
    );
};

export default TravelbingoEvaluationDetails;
